import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ContentActionsService } from '~commonInfra/contentActions/ContentActions';
import { SizeFilter } from '~commonInfra/utils/SizeFilter';
import { ChangeTagsActionHelperService } from '~content/services/ChangeTagsActionHelper';
import { siteUrlsSelector } from '~selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/app/AppStore';
import { SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { AppState } from '~src/app/state/AppState';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import {
  ConnectedDataFreshnessPolicyDetailSection,
  shouldShowDataFreshnessDialog,
} from '~src/dataAcceleration/components/DataFreshnessPolicyDetailSection';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { RevisionHistoryActionInstance } from '~src/revisionHistory/services/RevisionHistoryAction';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';
import {
  ModifiedAtDetailSection,
  ModifiedByDetailSection
} from '~src/spacesPlatform/components/content/contentDetailDialog/CommonContentDetailSections';
import { ContentDetailSection, sectionContainer } from '~src/spacesPlatform/components/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/spacesPlatform/components/content/contentDetailDialog/DeleteContentSection';
import {
  DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/spacesPlatform/components/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedLocationDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/LocationDetailSection';
import { ConnectedOwnerDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/OwnerDetailSection';
import { TagsDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/TagsDetailSection';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { TagsHelper } from '~src/ts/helpers/TagsInputHelper';
import { Set } from '~src/ts/models/Set';
import { IWorkbookDetailInfoWithUsage } from '~src/workbook/models/IWorkbookInfo';
import { IWorkbookActions } from '~src/workbook/ServerApi/types/IWorkbookActions';
import { ChangeWorkbookDescriptionAction } from '~src/workbook/services/ChangeWorkbookDescriptionAction';

import { ConnectedTabbedViewsDetailSection } from './TabbedViewsDetailSection';


interface WorkbookDetailsDialogBodyOwnProps {
  readonly workbook: IWorkbookDetailInfoWithUsage;
  readonly allowedActions: IWorkbookActions;
  readonly location: string;
}

interface WorkbookDetailsDialogBodyStateProps {
  readonly urls: SpacesSiteUrls;
}

interface WorkbookDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type WorkbookDetailsDialogBodyProps =
    WorkbookDetailsDialogBodyOwnProps &
    WorkbookDetailsDialogBodyStateProps &
    WorkbookDetailsDialogBodyDispatchProps;

export function WorkbookDetailsDialogBody(props: WorkbookDetailsDialogBodyProps): JSX.Element {
  const { workbook, urls, location, incrementRouterSpaceReactKey } = props;

  const setDescription = React.useCallback((description: string) => {
    return ChangeWorkbookDescriptionAction.execute(workbook, description);
  }, [workbook]);

  const setTags = React.useCallback((tags: string[]) => {
    return ChangeTagsActionHelperService.changeTagsForContent([workbook], TagsHelper.convertArrayToMap(workbook.tags), TagsHelper.convertArrayToMap(tags))
        .then((results: IResult[]) => ResultHelper.combineResults(results));
  }, [workbook]);

  const moveToProject = React.useCallback(() => {
    return ContentActionsService.moveWorkbooksToProject(Set.singletonForIdItem(workbook));
  }, [workbook]);

  const showRevisionHistory = React.useCallback(() => {
    return RevisionHistoryActionInstance.showWorkbookRevisionHistory(workbook);
  }, [workbook]);

  function allowedAction(actionName: keyof IWorkbookActions): boolean {
    return !!(props.allowedActions?.[actionName]);
  }

  const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
  const isOwner = workbook.owner? (userId === workbook.owner.id) : true;

  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={workbook.description}
        isActionEnabled={allowedAction('setDescription')}
        setDescription={setDescription}
        descriptionMaxLength={DESCRIPTION_MAX_LENGTH}
        showPreviewAndFormattingHints={true}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      {
        <ConnectedLocationDetailSection
        location={location}
        project={workbook.project}
        isActionEnabled={allowedAction('setProject')}
        content={workbook}
        urls={urls}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        isOwner={isOwner}
      />
      }
      <ConnectedOwnerDetailSection content={workbook} isActionEnabled={allowedAction('setOwner')} urls={urls}/>
      <TagsDetailSection
        tags={ TagsHelper.convertArrayToMap(workbook.tags)}
        isActionEnabled={allowedAction('setTags')}
        setTags={setTags}
        tagUrlFn={(tag: string) => (urls.workbooksWithTagUrl(tag))}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ConnectedTabbedViewsDetailSection workbook={workbook} isActionEnabled={allowedAction('setDisplayTabs')} />
      {shouldShowDataFreshnessDialog(workbook) &&
        <ConnectedDataFreshnessPolicyDetailSection
          workbook={workbook}
          editDataFreshnessAllowed={allowedAction('editDataFreshnessPolicy')}/>}
      <ContentDetailSection header={Messages.column_size()} detail={SizeFilter(workbook.size)} testId={'size'} />
      <ModifiedAtDetailSection
        date={workbook.updatedAt}
        allowShowRevisionHistory={allowedAction('versionManagement')}
        showRevisionHistory={showRevisionHistory}
      />
      {FeatureFlags.modifiedByUserEnabled &&
        <ModifiedByDetailSection user={workbook.modifiedBy} urls={urls} />
      }
      {allowedAction('del') &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.deleteWorkbook_action()}
          content={workbook}
        />
      }
    </div>
  );
}

const mapDispatchToProps: MapDispatchToProps<WorkbookDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<WorkbookDetailsDialogBodyStateProps> = (state: AppState, ownProps: WorkbookDetailsDialogBodyOwnProps) => ({
  urls: siteUrlsSelector(state),
});

export const ConnectedWorkbookDetailsDialogBody: React.ComponentClass<WorkbookDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(WorkbookDetailsDialogBody);
